import { Component, OnInit, Inject } from '@angular/core';
import { MatCheckbox, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';

import { environment } from '@environment';
import { MenuService } from '@core/services/menu/menu.service';
import { CartService } from '@core/services/cart/cart.service';
import { CustomizationService } from '@core/services/customization/customization.service';
import { UtilityService } from '@core/services/util/utility.service';
import { ToastrService } from '@core/services/notification/toastr.service';
import { PriceService } from '@core/services/price/price.service';

import { Product } from '@shared/models/product';

import { BaseComponent } from '@core/components/base/base.component';
import { ProductCustomizationType } from '@shared/models/product-customization-type';
import { GtmService } from '@core/services/analytics/gtm.service';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { MatRadioModule } from '@angular/material/radio';
import { PonchoUIModule } from '@poncho/ui';
import { DeferImageLoadDirective } from '../../directives/defer-image-load.directive';
import { FeatureToggleDirective } from '../../directives/feature-toggle.directive';
import { GroupCustomizationComponent } from './group-customization/group-customization.component';

export interface OldItemSelection {
    outlet_id?: number;
    menu_id?: number;
    quantity?: number;
    product_size_id?: number;
    customizations?: string;
}

export interface ItemSelection {
    product_size_id: number;
    customizations: { [customizationTypeId: number]: number[] };
    quantity: number;
    remark: string;
    oldSelection: OldItemSelection;
}

@Component({
    selector: 'app-customization',
    templateUrl: './customization.component.html',
    styleUrls: ['./customization.component.scss'],
    standalone: true,
    imports: [
        GroupCustomizationComponent,
        FeatureToggleDirective,
        MatDialogModule,
        DeferImageLoadDirective,
        PonchoUIModule,
        MatRadioModule,
        NgClass,
        NgTemplateOutlet,
        MatCheckboxModule
    ]
})
export class CustomizationComponent extends BaseComponent implements OnInit {
    product: Product;
    submitCartInProgress = false;
    selectedIndex = new Array(24).fill('');
    currentSelection: ItemSelection;
    currentRoute: any;
    mainProductImage: string;
    currentCustomization: any = {};
    menuData: any;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: any,
        private menuService: MenuService,
        private cartService: CartService,
        private priceService: PriceService,
        private customizationService: CustomizationService,
        private utilityService: UtilityService,
        private toastrService: ToastrService,
        private activatedRoute: ActivatedRoute,
        private dialogRef: MatDialogRef<CustomizationComponent>,
        private gtmService: GtmService
    ) {
        super();
        this.currentRoute = this._extractRouteData(this.activatedRoute);
    }

    ngOnInit() {
        const { product_id, ...restData } = this.data;
        const { outlet_id, menu_id, product_size_id, customization_ids, quantity, remark, isCustomize } = restData;
        const customizations = customization_ids;

        this.menuData = { outlet_id, menu_id, ...restData.menuData };

        this.product = this.menuData.products[product_id];
        this.currentSelection = {
            product_size_id: quantity
                ? product_size_id
                : this.menuService.getDefaultProductSize(product_id, this.menuData).id,
            customizations: quantity
                ? customizations &&
                  this.customizationService.convertCustomizationStringToCustomizationModel(customizations, this.menuData)
                : null,
            quantity: quantity || 1,
            remark: remark || '',
            oldSelection: isCustomize ? { outlet_id, menu_id, product_size_id, customizations, quantity } : null
        };

        if (this.currentSelection.customizations) {
            this.currentCustomization = this.currentSelection.customizations;
        }

        this.currentSelection.customizations = this.currentSelection.customizations || this.getDefaultCustomizations();

        const resolution = this.deviceService.getResolution('product');
        this.mainProductImage = environment.assetsUrl + `/rectangle-19x10/${resolution}/product/` + this.product.id;
    }

    private _extractRouteData(route: any) {
        let currentRoute = route;
        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
        }
        return currentRoute.data._value;
    }

    getDefaultCustomizations(): { [customizationTypeId: number]: number[] } {
        return this.customizationService.getDefaultCustomizations(this.getAllCustomizationType());
    }

    getFinalPrice(): number {
        const { product_size_id, customizations, quantity } = this.currentSelection;
        const customizationString = this.customizationService.getCustomizationString(customizations);
        return this.cartService.getCartItemPrice(this.menuData, product_size_id, customizationString, quantity);
    }

    toShowProductSizes(): boolean {
        return this.product.product_sizes.length > 1;
    }

    isSelected(type: string, id: number): boolean {
        const { product_size_id, customizations } = this.currentSelection;

        if (type === 'product_size') {
            return Boolean(id && product_size_id === id);
        }

        if (type === 'customization') {
            return this.customizationService.getCustomizationArray(customizations).includes(id);
        }

        return false;
    }

    getProductQuantity(): number {
        return this.currentSelection.quantity;
    }

    getAllCustomizationType(): ProductCustomizationType {
        const productSizeId = this.currentSelection.product_size_id;
        const customizationTypes = this.menuData.product_sizes[productSizeId].product_customization_types;
        return this.utilityService.sortBySequence(customizationTypes);
    }

    getCustomizations(typeId: number) {
        const customizations = this.menuData.product_customization_types[typeId].customizations;
        return this.utilityService.sortBySequence(customizations.filter(entity => !entity.sold_out));
    }

    updateQuantity(event: Event) {
        if (event.type === 'add') {
            const selectedQuantity = this.currentSelection.oldSelection
                ? this.currentSelection.quantity - this.currentSelection.oldSelection.quantity
                : this.currentSelection.quantity;
            if (
                this.currentSelection &&
                this.cartService.checkMaxLimit(
                    this.menuData.product_sizes[this.currentSelection.product_size_id].product_id,
                    this.menuData,
                    selectedQuantity
                )
            ) {
                this.currentSelection.quantity += 1;
            }
        }
        if (event.type === 'remove' && this.currentSelection.quantity > 1) {
            this.currentSelection.quantity -= 1;
        }
    }

    toggleProductSize(productSizeId: number) {
        this.currentSelection.product_size_id = productSizeId;
        this.currentSelection.customizations = this.getDefaultCustomizations();
    }

    toggleCustomization(customizationTypeId: number, customizationId: number, event: any = {}) {
        const customizationType = this.menuData.product_customization_types[customizationTypeId];
        const customization = this.menuData.product_customizations[customizationId];
        const customizations = new Set(this.currentSelection.customizations[customizationTypeId]);

        if (!customization) return; // should not ever happen, a check just to avoid data discrepancy in flow

        if (customizations.has(customizationId)) {
            if (customizations.size > customizationType.min) {
                customizations.delete(customizationId);
            }
        } else {
            if (customizationType.max === 1) {
                customizations.clear();
                customizations.add(customizationId);
            } else if (customizationType.max > customizations.size) {
                customizations.add(customizationId);
            } else if (customizationType.max === customizations.size) {
                this.gtmService.logWebAnalyticsEvent('customization', 'Max limit exceeded: ' + customizationType.label);
                this.toastrService.openSnackBar(
                    `Maximum ${customizationType.max} ${customizationType.label} is allowed.`,
                    'OKAY'
                );
            }
        }

        this.currentSelection.customizations[customizationTypeId] = Array.from(customizations);

        if (event && event.source instanceof MatCheckbox) {
            event.source.checked = customizations.has(customizationId);
        }
    }

    updateCart(selectionList?) {
        if (selectionList) {
            this.currentSelection['customizations'] = selectionList;
        }
        const selectionValidity = this.customizationService.isSelectionValid(this.currentSelection, this.menuData);
        // const isCartPage = (this.router.url === '/cart');

        if (selectionValidity.valid) {
            const { product_size_id, customizations, quantity, remark, oldSelection } = this.currentSelection;
            const customizationString = this.customizationService.getCustomizationString(customizations);

            const isItemAdded = this.cartService.addItem(this.product.id, product_size_id, customizationString, {
                quantity,
                remark,
                oldSelection,
                menuData: this.menuData
            });
            if (isItemAdded) {
                this.dialogRef.close();
            }
        } else {
            this.toastrService.openSnackBar(selectionValidity.message, 'OKAY');
        }
    }

    // product size price calculation
    getProductSizeMarkedPrice(productSize) {
        return this.priceService.getProductSizePrice(productSize).marked_price;
    }

    getProductSizePrice(productSize) {
        return this.priceService.getProductSizePrice(productSize).price;
    }

    close() {
        this.dialogRef.close();
    }

    getIntrinsicCustomizationLabel(customization_type) {
        const customizations = this.getCustomizations(customization_type.id);
        const label = customizations.reduce((label: string, customization: any, idx: number) => {
            if (idx === 0) {
                return customization.label;
            } else if (idx === customizations.length - 1) {
                return label + ' & ' + customization.label;
            } else {
                return label + ', ' + customization.label;
            }
        }, '');
        return label;
    }
}
